import { useState, useEffect, useCallback, useMemo } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../amplify/data/resource';
import { apiGet, apiPost } from '../lib/api';

interface ClientOption { id: string; name: string; }

interface Transaction {
  transactionId: string;
  clientId:      string;
  type:          string;
  creditsDelta:  number;
  dollarsAmount: number;
  packSize:      number;
  customerEmail: string;
  customerName:  string;
  stripeSessionId?: string;
  createdAt:     string;
}

const PACKS = [
  { pack: '1000',  credits: '1,000',  price: '$750',    perCredit: '$0.75', savings: null  },
  { pack: '2500',  credits: '2,500',  price: '$1,750',  perCredit: '$0.70', savings: '7%'  },
  { pack: '5000',  credits: '5,000',  price: '$3,250',  perCredit: '$0.65', savings: '13%' },
  { pack: '10000', credits: '10,000', price: '$6,000',  perCredit: '$0.60', savings: '20%' },
];

function fmt(n: number) { return new Intl.NumberFormat().format(n); }
function fmtDate(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function Credits({ clientId }: { clientId?: string }) {
  const db = useMemo(() => generateClient<Schema>(), []);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading,      setLoading]      = useState(false);
  const [buyLoading,   setBuyLoading]   = useState<string | null>(null);
  const [error,        setError]        = useState('');

  // Adjustment form state
  const [adjClientId,   setAdjClientId]   = useState('');
  const [adjDelta,      setAdjDelta]      = useState('');
  const [adjNote,       setAdjNote]       = useState('');
  const [adjType,       setAdjType]       = useState('usage');
  const [adjEmail,      setAdjEmail]      = useState('');
  const [adjName,       setAdjName]       = useState('');
  const [adjReceipt,    setAdjReceipt]    = useState(true);
  const [adjLoading,    setAdjLoading]    = useState(false);
  const [adjResult,     setAdjResult]     = useState<{ newBalance: number } | null>(null);
  const [clients,       setClients]       = useState<ClientOption[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet<{ transactions: Transaction[] }>('/credits/all-transactions');
      setTransactions(res.transactions);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    db.models.Client.list().then(({ data }) => {
      setClients(
        (data ?? [])
          .filter(c => c != null)
          .map(c => ({ id: c.id, name: c.clientName || c.id }))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    }).catch(() => {});
  }, []);

  async function buyCredits(pack: string) {
    setBuyLoading(pack);
    setError('');
    try {
      const { url } = await apiPost<{ url: string }>('/credits/checkout', { pack, clientId });
      window.location.href = url;
    } catch (e: any) {
      setError(e.message);
      setBuyLoading(null);
    }
  }

  async function submitAdjustment() {
    setAdjLoading(true);
    setAdjResult(null);
    setError('');
    try {
      const delta = parseFloat(adjDelta);
      if (!adjClientId) throw new Error('Client ID required');
      if (isNaN(delta) || delta === 0) throw new Error('Enter a non-zero credit amount');
      const res = await apiPost<{ newBalance: number }>('/credits/adjust', {
        clientId:      adjClientId,
        delta:         Math.round(delta),
        note:          adjNote,
        type:          adjType,
        sendReceipt:   adjReceipt,
        recipientEmail: adjEmail || undefined,
        recipientName:  adjName || undefined,
      });
      setAdjResult(res);
      setAdjDelta('');
      setAdjNote('');
      load(); // refresh table
    } catch (e: any) {
      setError(e.message);
    } finally {
      setAdjLoading(false);
    }
  }

  const totalCredits  = transactions.reduce((s, t) => s + (t.creditsDelta > 0 ? t.creditsDelta : 0), 0);
  const totalRevenue  = transactions.reduce((s, t) => s + t.dollarsAmount, 0);

  const card: React.CSSProperties = {
    background: '#0d1420', border: '1px solid #111927', borderRadius: 12, padding: 24,
  };

  const inp: React.CSSProperties = {
    width: '100%', background: '#080c14', border: '1px solid #1e2d42',
    borderRadius: 7, padding: '9px 12px', color: '#e8edf5', fontSize: 13,
    fontFamily: 'Sora, sans-serif', outline: 'none',
  };

  return (
    <div style={{ maxWidth: 900, display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Header */}
      <div>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#e8edf5' }}>B2B Credits</h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#4a5d7a' }}>
          1 credit = 1 verified business contact. Credits never expire.
        </p>
      </div>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                      borderRadius: 8, padding: '10px 14px', color: '#f87171', fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
        {[
          { label: 'Total Purchases',  value: String(transactions.length)    },
          { label: 'Total Credits Sold', value: fmt(totalCredits)            },
          { label: 'Total Revenue',    value: `$${(totalRevenue / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}` },
        ].map(s => (
          <div key={s.label} style={card}>
            <div style={{ fontSize: 11, color: '#3b82f6', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>
              {s.label}
            </div>
            <div style={{ fontSize: 26, fontWeight: 800, color: '#e8edf5', fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '-0.02em' }}>
              {loading ? '—' : s.value}
            </div>
          </div>
        ))}
      </div>

      {/* Manual adjustment */}
      <div style={card}>
        <div style={{ fontSize: 11, color: '#4a5d7a', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 20 }}>
          Manual Credit Adjustment
        </div>

        {adjResult && (
          <div style={{ background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)',
                        borderRadius: 8, padding: '10px 14px', color: '#34d399', fontSize: 13, marginBottom: 16 }}>
            Done — new balance: <strong>{fmt(adjResult.newBalance)} credits</strong>
            {adjReceipt && adjEmail && <span style={{ color: '#4a5d7a', marginLeft: 8 }}>Receipt sent to {adjEmail}</span>}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <label style={{ fontSize: 11, color: '#4a5d7a', letterSpacing: '0.07em', display: 'block', marginBottom: 5 }}>CLIENT ID *</label>
            <select value={adjClientId} onChange={e => setAdjClientId(e.target.value)} style={inp}>
              <option value="">— select a client —</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: '#4a5d7a', letterSpacing: '0.07em', display: 'block', marginBottom: 5 }}>CREDITS (negative to deduct) *</label>
            <input value={adjDelta} onChange={e => setAdjDelta(e.target.value)} placeholder="-500 or +1000" type="number"
              style={inp} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: '#4a5d7a', letterSpacing: '0.07em', display: 'block', marginBottom: 5 }}>TYPE</label>
            <select value={adjType} onChange={e => setAdjType(e.target.value)} style={inp}>
              <option value="usage">Usage (deduction)</option>
              <option value="manual_adjustment">Manual adjustment</option>
              <option value="refund">Refund</option>
              <option value="bonus">Bonus</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: '#4a5d7a', letterSpacing: '0.07em', display: 'block', marginBottom: 5 }}>NOTE (appears on receipt)</label>
            <input value={adjNote} onChange={e => setAdjNote(e.target.value)} placeholder="e.g. Campaign run - May 2026"
              style={inp} />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13, color: '#8a9bb5' }}>
            <input type="checkbox" checked={adjReceipt} onChange={e => setAdjReceipt(e.target.checked)}
              style={{ accentColor: '#3b82f6', width: 14, height: 14 }} />
            Send receipt email
          </label>
        </div>

        {adjReceipt && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 11, color: '#4a5d7a', letterSpacing: '0.07em', display: 'block', marginBottom: 5 }}>RECIPIENT EMAIL</label>
              <input value={adjEmail} onChange={e => setAdjEmail(e.target.value)} placeholder="client@example.com" type="email"
                style={inp} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#4a5d7a', letterSpacing: '0.07em', display: 'block', marginBottom: 5 }}>RECIPIENT NAME</label>
              <input value={adjName} onChange={e => setAdjName(e.target.value)} placeholder="John Smith"
                style={inp} />
            </div>
          </div>
        )}

        <button onClick={submitAdjustment} disabled={adjLoading} style={{
          background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)',
          borderRadius: 8, padding: '9px 22px', color: '#60a5fa',
          cursor: adjLoading ? 'not-allowed' : 'pointer',
          fontSize: 13, fontWeight: 600, fontFamily: 'Sora, sans-serif', opacity: adjLoading ? 0.6 : 1,
        }}>
          {adjLoading ? 'Applying…' : 'Apply Adjustment'}
        </button>
      </div>

      {/* All purchases */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: '#4a5d7a', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            All Credit Purchases
          </div>
          <button onClick={load} disabled={loading} style={{
            background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)',
            borderRadius: 6, padding: '5px 12px', color: '#60a5fa',
            cursor: 'pointer', fontSize: 12, fontFamily: 'Sora, sans-serif',
          }}>Refresh</button>
        </div>

        {transactions.length === 0 ? (
          <div style={{ color: '#3b4f6a', fontSize: 13, padding: '16px 0' }}>
            {loading ? 'Loading…' : 'No purchases yet.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #111927' }}>
                {['Date', 'Buyer', 'Client ID', 'Pack', 'Credits', 'Amount'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600,
                                       color: '#4a5d7a', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {transactions.map(tx => (
                <tr key={tx.transactionId} style={{ borderBottom: '1px solid #0d1a27' }}>
                  <td style={{ padding: '12px', color: '#8a9bb5', fontSize: 12 }}>{fmtDate(tx.createdAt)}</td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ fontSize: 13, color: '#e8edf5', fontWeight: 500 }}>{tx.customerName || '—'}</div>
                    <div style={{ fontSize: 11, color: '#4a5d7a' }}>{tx.customerEmail || '—'}</div>
                  </td>
                  <td style={{ padding: '12px', fontSize: 11, color: '#3b4f6a', fontFamily: 'IBM Plex Mono, monospace' }}>
                    {tx.clientId === 'unassigned' ? <span style={{ color: '#f59e0b' }}>unassigned</span> : tx.clientId}
                  </td>
                  <td style={{ padding: '12px', color: '#8a9bb5', fontSize: 13 }}>
                    {fmt(tx.packSize)} credits
                  </td>
                  <td style={{ padding: '12px', color: '#34d399', fontWeight: 600, fontSize: 14,
                               fontFamily: 'IBM Plex Mono, monospace' }}>
                    +{fmt(tx.creditsDelta)}
                  </td>
                  <td style={{ padding: '12px', color: '#e8edf5', fontSize: 13, fontWeight: 600 }}>
                    ${(tx.dollarsAmount / 100).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
